/**
 * Coach-side action panel used inside AdminClientProgressPage.
 *
 * Consolidates the three coach-driven workflows for a single client:
 *   1. Set the recurring report cadence (off / weekly / biweekly / monthly).
 *   2. Log a new body measurement on behalf of the client (offline sessions).
 *   3. Log a new workout entry on behalf of the client (offline sessions).
 *   4. Send a progress report right now with an optional personal note.
 *
 * All three are just thin dialog wrappers around the api.js helpers that
 * already exist (adminClientAddMeasurement, adminClientAddWorkoutLog,
 * adminClientReportSend, adminUserUpdateSettings).
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Ruler, Dumbbell, Send, Loader2, X, Save, Bell,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  adminClientAddMeasurement,
  adminClientAddWorkoutLog,
  adminUserUpdateSettings,
  adminClientReportSend,
} from '../services/api';

const CADENCE_LABELS = {
  off:       { label: 'Ki',           hint: 'Nincs automatikus riport' },
  weekly:    { label: 'Heti',         hint: 'Minden 7. napon' },
  biweekly:  { label: 'Kéthetente',   hint: 'Minden 14. napon' },
  monthly:   { label: 'Havonta',      hint: 'Minden hó elején' },
};

export default function AdminClientCoachPanel({ adminKey, userId, user, gender, onDataChanged }) {
  const initialCadence = user?.report_cadence || 'off';
  const [cadence, setCadence] = useState(initialCadence);
  const [cadenceSaving, setCadenceSaving] = useState(false);
  useEffect(() => { setCadence(user?.report_cadence || 'off'); }, [user?.report_cadence]);

  const saveCadence = async (val) => {
    setCadence(val);
    setCadenceSaving(true);
    try {
      await adminUserUpdateSettings(adminKey, userId, { report_cadence: val });
      toast.success(`Riport ütemezés: ${CADENCE_LABELS[val].label}`);
      onDataChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Nem sikerült menteni.');
      setCadence(initialCadence);
    } finally {
      setCadenceSaving(false);
    }
  };

  // Dialog states + shared submit-in-flight indicator.
  const [measOpen, setMeasOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  return (
    <div
      data-testid="coach-panel"
      className="bg-gray-900 border border-yellow-500/30 rounded-lg p-4 md:p-5 space-y-3"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-yellow-400 font-bold uppercase text-xs tracking-widest">
          <Bell size={14} /> Edzői panel — {user?.name || user?.email}
        </div>

        {/* Cadence selector — the change is auto-saved for fewer clicks. */}
        <div className="flex items-center gap-2">
          <Label className="text-gray-400 text-xs">Automatikus riport:</Label>
          <select
            data-testid="coach-cadence-select"
            value={cadence}
            disabled={cadenceSaving}
            onChange={(e) => saveCadence(e.target.value)}
            className="bg-gray-950 border border-gray-700 text-white text-sm rounded-md px-2 py-1.5 focus:outline-none focus:border-yellow-500 disabled:opacity-60"
          >
            {Object.entries(CADENCE_LABELS).map(([v, m]) => (
              <option key={v} value={v}>{m.label}</option>
            ))}
          </select>
          {cadenceSaving && <Loader2 className="animate-spin text-yellow-400" size={14} />}
        </div>
      </div>
      <div className="text-[11px] text-gray-500">
        {CADENCE_LABELS[cadence]?.hint} — {cadence === 'off' ? 'a klienshez nem megy automatikusan riport, de manuálisan bármikor küldhetsz.' : 'az ügyfél emailben megkapja az összegzést és a személyes üzenetedet.'}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2">
        <Button
          data-testid="coach-new-measurement-btn"
          onClick={() => setMeasOpen(true)}
          size="sm"
          className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
        >
          <Ruler size={14} className="mr-1.5" /> Új mérés rögzítése
        </Button>
        <Button
          data-testid="coach-new-workout-log-btn"
          onClick={() => setLogOpen(true)}
          size="sm"
          className="bg-sky-600 hover:bg-sky-500 text-white font-semibold"
        >
          <Dumbbell size={14} className="mr-1.5" /> Új napló bejegyzés
        </Button>
        <Button
          data-testid="coach-send-report-btn"
          onClick={() => setReportOpen(true)}
          size="sm"
          className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold"
        >
          <Send size={14} className="mr-1.5" /> Riport küldés most
        </Button>
      </div>

      <MeasurementDialog
        open={measOpen} onOpenChange={setMeasOpen} adminKey={adminKey} userId={userId}
        busy={busy} setBusy={setBusy} gender={gender} onSaved={onDataChanged}
      />
      <WorkoutLogDialog
        open={logOpen} onOpenChange={setLogOpen} adminKey={adminKey} userId={userId}
        busy={busy} setBusy={setBusy} onSaved={onDataChanged}
      />
      <ReportDialog
        open={reportOpen} onOpenChange={setReportOpen} adminKey={adminKey} userId={userId}
        clientName={user?.name || user?.email}
        busy={busy} setBusy={setBusy}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Measurement dialog                                                 */
/* ------------------------------------------------------------------ */
function MeasurementDialog({ open, onOpenChange, adminKey, userId, busy, setBusy, gender, onSaved }) {
  const [form, setForm] = useState({ weight_kg: '', waist_cm: '', neck_cm: '', hip_cm: '', shoulder_cm: '', note: '' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) setForm({ weight_kg: '', waist_cm: '', neck_cm: '', hip_cm: '', shoulder_cm: '', note: '' });
  }, [open]);

  // Fields shown depend on the client's gender so the coach records exactly
  // what the client sees in their portal (Adonis vs Venus index inputs).
  const fields = gender === 'male'
    ? [['weight_kg','Testsúly (kg)','pl. 78.4','coach-meas-weight'],
       ['waist_cm','Derékbőség (cm)','pl. 88.5','coach-meas-waist'],
       ['shoulder_cm','Vállkörméret (cm)','pl. 120','coach-meas-shoulder']]
    : gender === 'female'
    ? [['weight_kg','Testsúly (kg)','pl. 65.0','coach-meas-weight'],
       ['waist_cm','Derékbőség (cm)','pl. 72.0','coach-meas-waist'],
       ['hip_cm','Csípőbőség (cm)','pl. 96.0','coach-meas-hip']]
    : [['weight_kg','Testsúly (kg)','pl. 78.4','coach-meas-weight'],
       ['waist_cm','Derékbőség (cm)','pl. 88.5','coach-meas-waist'],
       ['neck_cm','Nyakbőség (cm)','pl. 39','coach-meas-neck'],
       ['hip_cm','Csípőbőség (cm)','pl. 98','coach-meas-hip']];

  const submit = async () => {
    const anyValue = fields.some(([k]) => form[k]);
    if (!anyValue) {
      toast.error('Legalább egy mérési adatot adj meg.');
      return;
    }
    setBusy(true);
    try {
      const payload = {};
      fields.forEach(([k]) => { if (form[k] !== '') payload[k] = parseFloat(form[k]); });
      if (form.note.trim()) payload.note = form.note.trim();
      await adminClientAddMeasurement(adminKey, userId, payload);
      toast.success('Mérés rögzítve.');
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Nem sikerült rögzíteni.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="coach-measurement-dialog" className="bg-gray-950 border-gray-800 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Ruler size={18} className="text-emerald-400" /> Új mérés rögzítése</DialogTitle>
          <DialogDescription className="text-gray-400 text-sm">
            {gender === 'male' && 'Férfi ügyfél — Adonis Index mezők (váll / derék).'}
            {gender === 'female' && 'Női ügyfél — Venus Index mezők (derék / csípő).'}
            {!gender && 'Ismeretlen nem — az összes klasszikus mérési mező.'}
            {' '}A mentés után azonnal megjelenik a grafikonján és a fiókjában.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            {fields.map(([k, label, placeholder, testid]) => (
              <div key={k}>
                <Label className="text-gray-400 text-xs">{label}</Label>
                <Input type="number" step="0.1" value={form[k]} onChange={e => set(k, e.target.value)} placeholder={placeholder} className="bg-gray-900 border-gray-700 mt-1" data-testid={testid} />
              </div>
            ))}
          </div>
          <div>
            <Label className="text-gray-400 text-xs">Megjegyzés (opcionális, max 300 karakter)</Label>
            <Textarea rows={2} value={form.note} onChange={e => set('note', e.target.value)} placeholder="pl. Reggeli mérés, jól hidratált." className="bg-gray-900 border-gray-700 mt-1" maxLength={300} />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-gray-700 text-gray-300"><X size={14} className="mr-1.5" /> Mégse</Button>
          <Button onClick={submit} disabled={busy} data-testid="coach-meas-submit" className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold">
            {busy ? <><Loader2 className="animate-spin mr-1.5" size={14} /> Mentés…</> : <><Save size={14} className="mr-1.5" /> Rögzítés</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Workout-log dialog                                                 */
/* ------------------------------------------------------------------ */
function WorkoutLogDialog({ open, onOpenChange, adminKey, userId, busy, setBusy, onSaved }) {
  const [form, setForm] = useState({
    exercise_name: '', weight_kg: '', reps: '', sets_done: '', rpe: '', note: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  useEffect(() => {
    if (open) setForm({ exercise_name: '', weight_kg: '', reps: '', sets_done: '', rpe: '', note: '' });
  }, [open]);

  const submit = async () => {
    if (!form.exercise_name.trim()) {
      toast.error('A gyakorlat neve kötelező.');
      return;
    }
    setBusy(true);
    try {
      const payload = { exercise_name: form.exercise_name.trim() };
      if (form.weight_kg !== '') payload.weight_kg = parseFloat(form.weight_kg);
      if (form.reps       !== '') payload.reps      = parseInt(form.reps, 10);
      if (form.sets_done  !== '') payload.sets_done = parseInt(form.sets_done, 10);
      if (form.rpe        !== '') payload.rpe       = parseFloat(form.rpe);
      if (form.note.trim())       payload.note      = form.note.trim();
      await adminClientAddWorkoutLog(adminKey, userId, payload);
      toast.success('Napló bejegyzés hozzáadva.');
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Nem sikerült hozzáadni.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="coach-workout-dialog" className="bg-gray-950 border-gray-800 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Dumbbell size={18} className="text-sky-400" /> Új edzésnapló bejegyzés</DialogTitle>
          <DialogDescription className="text-gray-400 text-sm">
            Egy gyakorlat legjobb sorozatát rögzítheted egyszerre. Több gyakorlathoz egyszerűen többször kattints — mind bekerül a napló panelbe és a grafikonba.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-gray-400 text-xs">Gyakorlat neve *</Label>
            <Input
              value={form.exercise_name}
              onChange={e => set('exercise_name', e.target.value)}
              placeholder="pl. Fekvenyomás, Guggolás, Deadlift"
              className="bg-gray-900 border-gray-700 mt-1"
              data-testid="coach-log-exercise"
            />
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div>
              <Label className="text-gray-400 text-xs">Súly (kg)</Label>
              <Input type="number" step="0.5" value={form.weight_kg} onChange={e => set('weight_kg', e.target.value)} placeholder="60" className="bg-gray-900 border-gray-700 mt-1" data-testid="coach-log-weight" />
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Ismétlés</Label>
              <Input type="number" min="0" value={form.reps} onChange={e => set('reps', e.target.value)} placeholder="8" className="bg-gray-900 border-gray-700 mt-1" data-testid="coach-log-reps" />
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Sorozat</Label>
              <Input type="number" min="0" value={form.sets_done} onChange={e => set('sets_done', e.target.value)} placeholder="3" className="bg-gray-900 border-gray-700 mt-1" data-testid="coach-log-sets" />
            </div>
            <div>
              <Label className="text-gray-400 text-xs">RPE (1-10)</Label>
              <Input
                type="number" min="1" max="10" step="0.5"
                value={form.rpe}
                onChange={e => set('rpe', e.target.value)}
                placeholder="8"
                className="bg-gray-900 border-gray-700 mt-1"
                data-testid="coach-log-rpe"
              />
            </div>
          </div>
          <div className="text-[10px] text-gray-500 -mt-2">
            RPE = szubjektív terhelésérzet. 10 = maximum (nulla ismétlés benn maradt), 7 ≈ 3 ismétlés még megvolna.
          </div>
          <div>
            <Label className="text-gray-400 text-xs">Megjegyzés (technika, energia, kedv)</Label>
            <Textarea
              rows={3}
              value={form.note}
              onChange={e => set('note', e.target.value)}
              placeholder="pl. Magas energia, tiszta technika. Kicsit fáradt volt a láb, jövő héten csökkentjük a volumenét."
              className="bg-gray-900 border-gray-700 mt-1"
              data-testid="coach-log-notes"
              maxLength={300}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-gray-700 text-gray-300"><X size={14} className="mr-1.5" /> Mégse</Button>
          <Button onClick={submit} disabled={busy} data-testid="coach-log-submit" className="bg-sky-600 hover:bg-sky-500 text-white font-semibold">
            {busy ? <><Loader2 className="animate-spin mr-1.5" size={14} /> Mentés…</> : <><Save size={14} className="mr-1.5" /> Hozzáadás</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Report send dialog with per-report coach note                      */
/* ------------------------------------------------------------------ */
function ReportDialog({ open, onOpenChange, adminKey, userId, clientName, busy, setBusy }) {
  const [note, setNote] = useState('');
  const [force, setForce] = useState(false);
  useEffect(() => { if (open) { setNote(''); setForce(false); } }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await adminClientReportSend(adminKey, userId, {
        coach_note: note.trim() || undefined,
        force: force || undefined,
      });
      if (res?.status === 'sent' || res?.status === 'mocked' || res?.ok) {
        toast.success('Riport elküldve.');
      } else if (res?.status === 'already_sent') {
        toast.info('Ebben a hónapban már ment riport — pipáld be az "Újraküldés" opciót, ha kell.');
      } else {
        toast.info('Riport feldolgozás elindult.');
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Nem sikerült elküldeni a riportot.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="coach-report-dialog" className="bg-gray-950 border-gray-800 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send size={18} className="text-yellow-400" /> Riport küldés — {clientName}</DialogTitle>
          <DialogDescription className="text-gray-400 text-sm">
            Előző hónapi mérések, edzések, súlyváltozás és a személyes üzeneted egy prémium PDF-fel az ügyfél email címére.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-gray-400 text-xs">Személyes üzenet a riporthoz (opcionális)</Label>
            <Textarea
              data-testid="coach-report-note"
              rows={5}
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="pl. Anna, gratulálok a −2.4 kg-hoz! Emeljük a fehérjebevitelt 1.8 g/kg-ra, és a jövő hónapban lássuk a hipertrófiafázist. — Gabi"
              className="bg-gray-900 border-gray-700 mt-1"
              maxLength={2000}
            />
            <div className="text-[11px] text-gray-500 mt-1">{note.length}/2000 karakter — az email törzsében kiemelten jelenik meg.</div>
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
            <input type="checkbox" checked={force} onChange={e => setForce(e.target.checked)} className="accent-yellow-500" data-testid="coach-report-force" />
            Újraküldés — még akkor is menjen, ha ebben a hónapban már ment egy.
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-gray-700 text-gray-300"><X size={14} className="mr-1.5" /> Mégse</Button>
          <Button onClick={submit} disabled={busy} data-testid="coach-report-submit" className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold">
            {busy ? <><Loader2 className="animate-spin mr-1.5" size={14} /> Küldés…</> : <><Send size={14} className="mr-1.5" /> Elküldés</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
